/**
 * Where a previewed file CAME FROM — and the one control that acts on it
 * (owner directive 2026-08-24, round 2).
 *
 * A preview is a window onto a file, but a file on this platform always has a
 * story: the conversation it was produced in or dropped into. This module
 * carries that origin through every preview shell and offers the single
 * control that reunites the two — "open chat", which brings Prest0n up as the
 * docked right rail while the preview keeps the rest of the screen
 * (FilePreviewOverlay's companion layout does the reflow; App's
 * RoutedContentShell moves the page under both).
 *
 * ONE component for BOTH shells (modal + floating window) on purpose: the
 * copy, the tooltip and the dispatch can never drift between them.
 *
 * Origin is best-effort by design. The chat's own attachment cards know the
 * thread for free (the transcript on screen IS the active thread); a file
 * opened from a bare link knows nothing. Both are useful: with a thread the
 * chat returns to that exact conversation, without one it simply comes up
 * beside the preview on whatever conversation is already open.
 */
import React from 'react';
import { ToolButton } from './PreviewContent';
import { requestChatOpenForThread } from '../GlobalChatSystem/openChatForFile';
import type { AttachmentResolveContext } from './resolveAttachment';

/**
 * Extends the server-resolver context (messageId/sessionId/turnId — used to
 * recover a locator-free attachment's bytes) with the CONVERSATION identity
 * the companion control needs. One object travels with a preview so a shell
 * never has to choose which of two context bags to forward.
 */
export interface PreviewOriginContext extends AttachmentResolveContext {
  /** Thread the file was previewed from, when the opener knew it. */
  threadId?: string;
  /** That thread's title, for switchThread's minimal-record path. */
  threadTitle?: string;
}

/**
 * Ask the chat to come up beside this preview, on the file's own
 * conversation when we know it. Returns true when a visible chat took it.
 */
export function openChatBesideFile(origin?: PreviewOriginContext | null): boolean {
  return requestChatOpenForThread({
    threadId: origin?.threadId,
    fallbackTitle: origin?.threadTitle,
    // The whole point of the control: the pane docks right and this preview
    // keeps the left. A floating/centered pane would cover the file.
    forceDocked: true,
  });
}

export interface OpenChatBesideButtonProps {
  origin?: PreviewOriginContext | null;
  /** Fired after a visible chat claims the request (shells re-place themselves). */
  onOpened?: () => void;
}

/**
 * The control itself. Wears the shells' shared ToolButton chrome, so it reads
 * as one of the preview's own tools rather than a bolted-on affordance.
 */
export const OpenChatBesideButton: React.FC<OpenChatBesideButtonProps> = ({ origin, onOpened }) => (
  <ToolButton
    data-preview-open-chat=""
    aria-label={origin?.threadId
      ? 'open the conversation this file came from, beside this preview'
      : 'open the chat beside this preview'}
    title={origin?.threadId
      ? 'Open the conversation this file came from — Prest0n docks on the right, this preview keeps the rest of the screen'
      : 'Open Prest0n on the right and keep this preview beside it'}
    // The floating window drags by its header: a pointer-down on a header
    // button must not also start a window drag (sibling tools do the same).
    onPointerDown={(e: React.PointerEvent) => e.stopPropagation()}
    onClick={(e: React.MouseEvent) => {
      e.stopPropagation();
      if (openChatBesideFile(origin)) onOpened?.();
    }}
  >
    Open chat
  </ToolButton>
);

export default OpenChatBesideButton;
